import React, { useState, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { Search, X, MapPin, User, FileText, ArrowUpRight } from "lucide-react";
import { forSaleListings, salesListings, recentSold, teamMembers, homeSearchAreas } from "../../mock";

const buildIndex = () => {
  const raw = [
    ...forSaleListings.map((p) => ({ name: p.name, address: p.address, price: p.price, route: "/buy" })),
    ...salesListings.map((p) => ({ name: p.name, address: p.address, price: p.price, route: "/#recent-sold" })),
    ...recentSold.map((p) => ({ name: p.name, address: p.address, price: p.price, route: "/#recent-sold" })),
  ];
  const seen = new Set();
  const properties = raw.filter((p) => {
    const k = `${p.name}-${p.address}`;
    if (seen.has(k)) return false;
    seen.add(k);
    return true;
  });
  const agents = teamMembers.map((m) => ({ title: m.name, sub: m.role, route: `/team/${m.slug}` }));
  const pages = [
    { title: "Home", sub: "Start here", route: "/" },
    { title: "Buy with Capital Corner", sub: "Active listings & search", route: "/buy" },
    { title: "Sell with Capital Corner", sub: "Home valuation & marketing", route: "/sell" },
    { title: "Meet the Team", sub: "Our advisors", route: "/team" },
    { title: "Developers", sub: "Builder partnerships", route: "/developers" },
    { title: "Contact", sub: "Get in touch", route: "/#contact" },
    ...homeSearchAreas.map((l) => ({ title: l.label, sub: "Home search", route: l.to })),
  ];
  return { properties, agents, pages };
};

const Group = ({ icon: Icon, label, items, render, onGo }) =>
  items.length > 0 ? (
    <div className="mb-6">
      <p className="text-[10px] tracking-luxe uppercase text-[#b4936a] mb-2 flex items-center gap-2 px-1"><Icon size={13} /> {label}</p>
      <div className="grid sm:grid-cols-2 gap-1">
        {items.map((it, i) => (
          <button key={i} onClick={() => onGo(it.route)} className="text-left group flex items-center justify-between gap-3 py-2.5 px-3 rounded hover:bg-neutral-100 transition-colors">
            {render(it)}
            <ArrowUpRight size={15} className="text-neutral-300 group-hover:text-[#b4936a] transition-colors shrink-0" />
          </button>
        ))}
      </div>
    </div>
  ) : null;

const SearchOverlay = ({ open, onClose }) => {
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const inputRef = useRef(null);
  const index = useMemo(buildIndex, []);

  useEffect(() => {
    if (!open) return;
    setQ("");
    const t = setTimeout(() => inputRef.current && inputRef.current.focus(), 80);
    document.body.style.overflow = "hidden";
    const onKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", onKey);
    return () => {
      clearTimeout(t);
      window.removeEventListener("keydown", onKey);
      document.body.style.overflow = "";
    };
  }, [open, onClose]);

  const term = q.trim().toLowerCase();
  const results = useMemo(() => {
    if (!term) return { properties: [], agents: [], pages: [] };
    const m = (s) => s && s.toLowerCase().includes(term);
    return {
      properties: index.properties.filter((p) => m(p.name) || m(p.address) || m(p.price)).slice(0, 8),
      agents: index.agents.filter((a) => m(a.title) || m(a.sub)).slice(0, 6),
      pages: index.pages.filter((p) => m(p.title) || m(p.sub)).slice(0, 8),
    };
  }, [term, index]);

  const go = (route) => { onClose(); setTimeout(() => navigate(route), 0); };
  const total = results.properties.length + results.agents.length + results.pages.length;

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-[95] flex items-start justify-center p-4 md:p-8" onMouseDown={onClose}>
      <div className="absolute inset-0 bg-black/70 backdrop-blur-sm" />
      <div
        className="relative w-full max-w-[760px] mt-16 md:mt-24 bg-white rounded-xl shadow-2xl overflow-hidden"
        onMouseDown={(e) => e.stopPropagation()}
      >
        {/* Input bar */}
        <div className="flex items-center gap-3 px-5 border-b border-neutral-200">
          <Search size={22} className="text-[#b4936a] shrink-0" strokeWidth={1.6} />
          <input
            ref={inputRef}
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search properties, advisors, pages, cities..."
            className="flex-1 bg-transparent outline-none py-5 text-lg md:text-xl text-neutral-900 placeholder:text-neutral-400"
          />
          <button onClick={onClose} className="text-neutral-400 hover:text-neutral-900 transition-colors shrink-0" aria-label="Close search">
            <X size={24} />
          </button>
        </div>

        {/* Results */}
        <div className="max-h-[60vh] overflow-y-auto p-4 md:p-5">
          {!term && (
            <p className="text-neutral-400 text-[13px] px-1 py-6 text-center">Start typing to search across the entire site &mdash; homes, advisors, pages and neighborhoods.</p>
          )}
          {term && total === 0 && (
            <p className="text-neutral-500 text-center py-8 font-serif-display text-xl">No results for &ldquo;{q}&rdquo;</p>
          )}
          <Group icon={MapPin} label="Properties" items={results.properties} onGo={go} render={(p) => (
            <span><span className="block font-serif-display text-[17px] text-neutral-900">{p.name}{p.price ? ` \u2014 ${p.price}` : ""}</span><span className="block text-neutral-400 text-[12px]">{p.address}</span></span>
          )} />
          <Group icon={User} label="Advisors" items={results.agents} onGo={go} render={(a) => (
            <span><span className="block font-serif-display text-[17px] text-neutral-900">{a.title}</span><span className="block text-neutral-400 text-[12px]">{a.sub}</span></span>
          )} />
          <Group icon={FileText} label="Pages" items={results.pages} onGo={go} render={(p) => (
            <span><span className="block font-serif-display text-[17px] text-neutral-900">{p.title}</span><span className="block text-neutral-400 text-[12px]">{p.sub}</span></span>
          )} />
        </div>
      </div>
    </div>
  );
};

export default SearchOverlay;
